import { defaultTheme, useHelmet } from '@agro-club/agroclub-shared'
import { useAnalyticsContext } from 'analytics/hooks'
import { format } from 'date-fns'
import { useFilters } from 'hooks/useFilter'
import { YEAR_MONTH } from 'modules/constants'
import { AuthContext } from 'modules/context/AuthContext'
import { DealCondition } from 'modules/domain/deal/types'
import { ListRequestParams } from 'modules/domain/types'
import { getMonthList } from 'modules/utils/helpers'
import { FC, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'
import { generatePath, useNavigate, useParams } from 'react-router'
import { AAntdTabs } from 'views/components/Analytics'
import APageWrapper from 'views/components/PageWrapper/APageWrapper'
import * as Layout from 'views/layouts/NewLayout'
import * as Header from 'views/ui/Header/Header'
import { AnalyticsPages, AnalyticsPlaces } from './analyticsPlaces'
import { Board } from './Board/Board'
import { DealFilters } from './Filters/DealFilters'
import DealRoutes from './routes'

export const defaultListRequestParams: ListRequestParams = {
  page: 1,
  filter: {},
  pageSize: 10000,
}

export const DealList: FC = () => {
  const { t } = useTranslation('deal')
  const { setOptions } = useAnalyticsContext()
  useHelmet({ title: t('title') })
  const navigate = useNavigate()
  const { profile: currentUser } = useContext(AuthContext)

  const { activeTab: activeTabParams, selectedDeal: selectedDealParams } = useParams()
  const [activeTab, setActiveTab] = useState<string>(activeTabParams || DealCondition.open)

  const [listRequestParams, setListRequestParams] = useState<ListRequestParams>({
    ...defaultListRequestParams,
  })

  const defaultFilterValues = useMemo(
    () => ({
      ...(currentUser?.profile?.team?.id && { team: String(currentUser.profile.team.id) }),
      month: format(getMonthList()[0], YEAR_MONTH),
    }),
    [currentUser],
  )

  const { isFiltersLoaded, updateFilterState } = useFilters({
    listRequestParams,
    setListRequestParams,
    defaultParams: defaultFilterValues,
  })

  const changeRoute = () => {
    navigate(
      {
        pathname: generatePath(DealRoutes.ListWithTab, {
          activeTab: activeTab,
        }),
        search: location.search,
      },
      { replace: true },
    )
    setOptions({
      place: activeTab,
    })
  }

  useEffect(() => {
    if (selectedDealParams) return
    changeRoute()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  const changeActiveTab = (type) => {
    setActiveTab(type)

    // wee need to reset selected tab when tab is changed
    if (selectedDealParams) {
      changeRoute()
    }
  }

  const isMobile = useMediaQuery({
    query: `(max-width: ${defaultTheme.queries.mobile})`,
  })

  return (
    <APageWrapper page={AnalyticsPages.DEALS} place={AnalyticsPlaces.LIST}>
      <Layout.WrapperContent>
        <Layout.Header>
          {!isMobile && (
            <Layout.TopHeader>
              <Header.Breadcrumbs routes={[{ breadcrumbName: t('title') }]} />
            </Layout.TopHeader>
          )}

          <Layout.PageName>{t('title')}</Layout.PageName>

          <DealFilters
            defaultFilterValues={defaultFilterValues}
            setListRequestParams={setListRequestParams}
            updateFilterState={updateFilterState}
            listRequestParams={listRequestParams}
            isFiltersLoaded={isFiltersLoaded}
          />
        </Layout.Header>
        <AAntdTabs
          id="tabs"
          items={[
            {
              label: t('dealCondition.open'),
              key: DealCondition.open,
              children: (
                <Board
                  listRequestParams={listRequestParams}
                  setListRequestParams={setListRequestParams}
                  isFiltersLoaded={isFiltersLoaded}
                  typeDeals={DealCondition.open}
                  activeTab={activeTab}
                />
              ),
            },
            {
              label: t('dealCondition.closed'),
              key: DealCondition.closed,
              children: (
                <Board
                  listRequestParams={listRequestParams}
                  isFiltersLoaded={isFiltersLoaded}
                  typeDeals={DealCondition.closed}
                  activeTab={activeTab}
                />
              ),
            },
          ]}
          onChange={changeActiveTab}
          activeKey={activeTab}
        />
      </Layout.WrapperContent>
    </APageWrapper>
  )
}
